import { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export default function AdminPush() {
  const [state, setState] = useState("loading"); // loading | unsupported | off | on
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setState("unsupported");
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setState(sub ? "on" : "off");
    } catch {
      setState("off");
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/push/vapid-key");
      const reg = await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Benachrichtigungen wurden blockiert. Auf dem iPhone: Einstellungen → Apps → JUSTIN HILFT → Mitteilungen erlauben.");
        setBusy(false);
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await api.post("/push/subscribe", sub.toJSON());
      setState("on");
      toast.success("Push-Benachrichtigungen aktiviert — teste es mit dem Button unten.");
    } catch (err) {
      const msg = err?.message || "";
      if (msg.includes("incognito") || msg.includes("Push API")) {
        toast.error("Push geht nicht im privaten/Inkognito-Fenster — bitte im normalen Browser bzw. über die Home-Bildschirm-App öffnen.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/subscribe", { data: sub.toJSON() });
        await sub.unsubscribe();
      }
      setState("off");
      toast.success("Push-Benachrichtigungen deaktiviert.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      await api.post("/push/test");
      toast.success("Test-Benachrichtigung wurde an dein Gerät geschickt.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="flex items-center gap-3 text-zinc-500" data-testid="push-loading">
        <Loader2 size={16} className="animate-spin" /> Prüfe Push-Status ...
      </div>
    );
  }

  if (state === "unsupported") {
    return (
      <div className="border border-white/10 p-5 space-y-2" data-testid="push-unsupported">
        <p className="text-sm text-zinc-300 flex items-center gap-2"><BellOff size={16} className="text-zinc-500" /> Push nicht verfügbar</p>
        <p className="text-xs text-zinc-500 leading-relaxed">
          Auf dem iPhone geht das erst, wenn du die Website zum Home-Bildschirm hinzugefügt hast
          (Safari → Teilen → „Zum Home-Bildschirm") und dann die App vom Home-Bildschirm öffnest.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-white/10 p-5 space-y-4" data-testid="push-settings">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-zinc-300 flex items-center gap-2">
          {state === "on" ? <BellRing size={16} className="text-goldlight" /> : <Bell size={16} className="text-zinc-500" />}
          {state === "on" ? "Push-Benachrichtigungen aktiv" : "Push-Benachrichtigungen aus"}
        </p>
        <span
          className={`text-[10px] uppercase tracking-[0.2em] border px-2.5 py-1 ${state === "on" ? "border-emerald-600/40 text-emerald-400" : "border-white/10 text-zinc-500"}`}
          data-testid="push-status-badge"
        >
          {state === "on" ? "Aktiv" : "Inaktiv"}
        </span>
      </div>
      <p className="text-xs text-zinc-500 leading-relaxed">
        Du bekommst sofort eine Mitteilung auf den Home-Bildschirm bei: neuer Buchung, Zahlungseingang,
        neuer Anfrage, neuer Bewertung und Login-Alarm. Kostenlos, kein E-Mail-Limit nötig.
      </p>
      <div className="flex flex-wrap gap-2">
        {state === "off" ? (
          <button
            onClick={enable}
            disabled={busy}
            data-testid="push-enable-btn"
            className="text-[11px] uppercase tracking-[0.2em] text-coal bg-goldlight px-4 py-2.5 transition-opacity duration-300 hover:opacity-85 disabled:opacity-50 flex items-center gap-2"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />} Auf diesem Gerät aktivieren
          </button>
        ) : (
          <>
            <button
              onClick={test}
              disabled={busy}
              data-testid="push-test-btn"
              className="text-[11px] uppercase tracking-[0.2em] text-goldlight border border-goldlight/40 px-4 py-2 transition-colors duration-300 hover:bg-goldlight hover:text-coal flex items-center gap-2"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <BellRing size={13} />} Test senden
            </button>
            <button
              onClick={disable}
              disabled={busy}
              data-testid="push-disable-btn"
              className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-red-400 border border-white/10 hover:border-red-500/40 px-4 py-2 transition-colors duration-300"
            >
              Deaktivieren
            </button>
          </>
        )}
      </div>
    </div>
  );
}
